<script setup lang="ts">
import WebsitesService from '../../pages/cms/management/websites/_runtime/services/WebsitesService';
import type { PageListItemModel } from '../../pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '../../pages/cms/management/pages/_runtime/services/PagesService';
import PreviewService from '../../runtime/services/PreviewService';
import { useWebsitesStore } from '../../pages/cms/management/websites/_runtime/stores/websites';

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
    (e: 'preview', url: string): void;
}>();

const props = withDefaults(defineProps<{
    modelValue?: number;
    module?: string;
    disabled?: boolean;
    preview?: boolean;
    website?: boolean;
    label?: string;
    noPreview?: boolean;
    websiteClass?: string;
}>(), {
    modelValue: null,
    module: '',
    disabled: false,
    preview: true,
    website: true,
    label: '[[[Publikuj na stronie]]]',
    noPreview: false,
    websiteClass: ''
});

const store = useWebsitesStore();
const { $log } = useLogging();
const { $alert } = useAlerts();

const pages = ref<PageListItemModel[]>([]);
const current = computed<CurrentWebsite>(() =>
{
    return store.current;
});

const pageId = computed({
    get(): number
    {
        return props.modelValue;
    },
    set(value: number)
    {
        triggerModel(value);
    }
});

const page = computed<any>(() =>
{
    return pages.value.find(p => p.id == pageId.value);
});

function triggerModel(value: number): void
{
    emit('update:modelValue', value);
}

async function loadPages(): Promise<void>
{
    try
    {
        const response = await PagesService.getPagesByModule(current.value.website, current.value.locale, props.module);

        pages.value = response.map((p) =>
        {
            p.disabled = !p.module || props.module !== p.module;

            return p;
        });
    }
    catch (ex)
    {
        pages.value = [];
        $log.debug(ex);
    }
}

async function handleWebsiteChange(): Promise<void>
{
    pageId.value = null;
    await loadPages();
}

async function handleLocaleChange(): Promise<void>
{
    pageId.value = null;
    await loadPages();
}

async function onPreview(): Promise<string>
{
    try
    {
        const token = await PreviewService.getPreviewToken();
        const website = (await WebsitesService.fetch(current.value.website)).result;
        const scheme = website.isSecured ? 'https://' : 'http://';
        const url = `${scheme}${website.baseUrl}/preview/${current.value.locale}/${pageId.value}/{0}?access_token=${token}`;

        emit('preview', url);

        return url;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

onBeforeMount(async (): Promise<void> =>
{
    await wait(() => current.value.website);
    await loadPages();
});

defineExpose({
    page,
    onPreview
});
</script>

<template>
    <IdeoFormGroup :label="$t(label)" style="container-type: inline-size;">
        <div class="page-selector d-flex gap-2">
            <EditoWebsiteSelector v-if="website" :disabled="disabled" :class="websiteClass" @change-website="handleWebsiteChange" @change-locale="handleLocaleChange" />
            <div class="flex-fill">
                <IdeoFormTreeSelect v-model="pageId" :disabled="disabled" :options="pages" transform :empty-option="$t('[[[Wybierz stronę]]]')" />
            </div>
            <div v-if="!noPreview">
                <IdeoButton v-if="preview" variant="info" :title="$t('[[[Zobacz przed opublikowaniem]]]')" :disabled="!pageId || disabled" @click="onPreview()">
                    <i class="fas fa-fw fa-search" />
                </IdeoButton>
            </div>
        </div>
    </IdeoFormGroup>
</template>

<style>
@container (max-width: 400px) {
    .page-selector {
        flex-wrap: wrap;
    }
}
</style>
